import { zodResolver } from '@hookform/resolvers/zod';
import { DetailDrawer } from '@rossum/ui';
import { IconAlertTriangle } from '@rossum/ui/icons/tabler';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  RadioGroup,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import DOMPurify from 'dompurify';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { notify } from '../../../../../../toaster';
import { ExternalHookTemplate } from '../customExtensionSchema';
import { useInstall } from '../hooks/useInstall';
import { useVersions } from '../hooks/useVersions';
import { ExtensionVersionOption } from './ExtensionVersionOption';

export type CustomExtensionDrawerState = {
  externalHookTemplateId: string;
  hookInterfaceId: number;
};

type CustomExtensionDrawerProps = {
  selectedHookTemplate: CustomExtensionDrawerState | undefined;
  hookTemplate: ExternalHookTemplate | undefined;
  onClose: () => void;
};

export const CustomExtensionDrawer = ({
  selectedHookTemplate,
  hookTemplate,
  onClose,
}: CustomExtensionDrawerProps) => {
  const intl = useIntl();
  const { mutate: install, isLoading } = useInstall();

  const { data: versions, isLoading: isLoadingVersions } = useVersions(
    selectedHookTemplate ?? null
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      version: null,
    },
    resolver: zodResolver(z.object({ version: z.string().trim().min(1) })),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <DetailDrawer
      open={!!selectedHookTemplate}
      onClose={handleClose}
      title={hookTemplate?.name}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit(({ version }) => {
          if (version && selectedHookTemplate) {
            install(
              {
                version,
                ...selectedHookTemplate,
              },
              {
                onSuccess: () => {
                  notify.success({
                    title: intl.formatMessage({
                      id: 'features.externalStore.message.installed',
                    }),
                  });
                  onClose();
                },
                onError: () => {
                  notify.error({
                    title: intl.formatMessage({
                      id: 'features.externalStore.message.error',
                    }),
                  });
                },
              }
            );
          }
        })}
        sx={{ flex: 1, px: 3, py: 2 }}
      >
        <Stack spacing={3} mt={2}>
          {hookTemplate ? (
            <Typography
              component="div"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(hookTemplate?.store_description),
              }}
            />
          ) : null}
          {isLoadingVersions ? (
            <>
              <Skeleton variant="rounded" width={120} height={20} />
              <Skeleton variant="rounded" width="100%" height={50} />
            </>
          ) : versions && versions.length > 0 ? (
            <>
              <Typography>
                {intl.formatMessage({
                  id: 'features.externalStore.drawer.chooseVersion',
                })}
              </Typography>

              <Controller
                control={control}
                name="version"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onChange={(_, val) => field.onChange(val)}
                    sx={{ gap: 2 }}
                  >
                    {versions.map((version, index) => {
                      const isLatest = index === 0;
                      return (
                        <ExtensionVersionOption
                          key={version}
                          value={version}
                          label={`v${version}`}
                          description={
                            isLatest
                              ? intl.formatMessage({
                                  id: 'features.externalStore.drawer.latestVersion',
                                })
                              : undefined
                          }
                          isLatest={isLatest}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
              />
            </>
          ) : (
            <Card variant="outlined">
              <CardContent>
                <Stack
                  sx={{
                    gap: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <SvgIcon sx={{ color: 'text.secondary' }}>
                    <IconAlertTriangle />
                  </SvgIcon>
                  <Stack sx={{ textAlign: 'center' }}>
                    <Typography>
                      {intl.formatMessage({
                        id: 'features.externalStore.drawer.noVersionsFound',
                      })}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
        <Button
          type="submit"
          startIcon={
            isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          variant="contained"
          sx={{ alignSelf: 'flex-end', mt: 'auto' }}
          disabled={!isValid}
        >
          {intl.formatMessage({
            id: 'features.externalStore.drawer.install',
          })}
        </Button>
      </Stack>
    </DetailDrawer>
  );
};
